export class WebSocketService {
    private socket: WebSocket | null = null
    private reconnectAttempts: number = 0
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null
    private shouldBeConnected: boolean = false
    private listeners: Map<string, ((data: unknown) => void)[]> = new Map()
    private jwtToken: string | null = null
    private isConnecting: boolean = false

    constructor(private url: string) {
        this.url = url
        this.jwtToken = null
    }

    // Set JWT token for authentication
    public setAuthToken(token: string): void {
        const prevToken = this.jwtToken
        this.jwtToken = token

        // If already connected, reconnect with the new token
        if (this.socket && this.socket.readyState === WebSocket.OPEN && prevToken !== this.jwtToken) {
            this.disconnect()
            this.connect()
        }
    }

    public connect(): void {
        this.shouldBeConnected = true

        if (this.isConnecting) {
            //console.log('WebSocket connection attempt already in progress');
            return
        }

        if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
            //console.log('WebSocket already connected or connecting');
            return
        }

        this.isConnecting = true

        try {
            // Create a custom WebSocket object with headers
            // Unfortunately, the WebSocket constructor doesn't accept headers directly
            // So we need to send the token right after connection is established
            const socket = new WebSocket(this.url)
            this.socket = socket

            socket.onopen = () => {
                this.isConnecting = false

                // Send authentication message as the first message after connection
                // We have 5 seconds to send the auth message after connection is established
                this.sendAuthMessage()
            }

            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)

                    // Only a successful login ends the reconnect backoff (the server closes on a refused token)
                    if (data.type === 'auth_success') {
                        this.reconnectAttempts = 0
                    }

                    // Dispatch message to all registered listeners for this event type
                    if (data.action && this.listeners.has(data.action)) {
                        this.listeners.get(data.action)?.forEach((listener) => listener(data))
                    }

                    // Also dispatch to any "*" listeners
                    if (this.listeners.has('*')) {
                        this.listeners.get('*')?.forEach((listener) => listener(data))
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error)
                }
            }

            socket.onclose = (event) => {
                if (this.socket !== socket) return // an old socket, already replaced or disconnected

                this.isConnecting = false

                // Also reconnect after a clean close (f.ex. server restart or auth refused):
                // only disconnect() means the app doesn't want a connection anymore
                if (this.shouldBeConnected) {
                    console.log(`WebSocket connection closed (code: ${event.code}). Attempting to reconnect...`)
                    this.attemptReconnect()
                }
            }

            socket.onerror = (error) => {
                this.isConnecting = false
                console.error('WebSocket error:', error)
                // Don't close the socket here as the onclose handler will be called automatically
            }
        } catch (error) {
            this.isConnecting = false
            console.error('Error creating WebSocket connection:', error)
            this.attemptReconnect()
        }
    }

    private sendAuthMessage(): void {
        if (this.socket?.readyState === WebSocket.OPEN && this.jwtToken) {
            // Send the authentication message with the JWT token
            this.socket.send(
                JSON.stringify({
                    type: 'auth',
                    token: this.jwtToken,
                }),
            )
        }
    }

    // Keeps trying: 5s, 10s, 20s, 40s and then every 60s
    private attemptReconnect(): void {
        if (this.reconnectTimer || !this.shouldBeConnected) return

        const delay = Math.min(5000 * 2 ** this.reconnectAttempts, 60000)
        this.reconnectAttempts++
        console.log(`Attempting to reconnect (${this.reconnectAttempts}) in ${delay / 1000}s...`)

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null
            this.connect()
        }, delay)
    }

    /**
     * Reconnect now when the connection is gone (f.ex. tab visible again, laptop back from sleep, network back),
     * instead of waiting for the next reconnect attempt.
     */
    public ensureConnected(): void {
        if (!this.shouldBeConnected || this.isConnected() || this.isConnecting) return

        if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
        this.reconnectAttempts = 0
        this.connect()
    }

    public disconnect(): void {
        this.shouldBeConnected = false
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
        this.reconnectAttempts = 0
        this.isConnecting = false

        if (this.socket) {
            if (this.socket.readyState === WebSocket.OPEN) {
                try {
                    this.socket.send(JSON.stringify({ type: 'disconnect' }))
                } catch {
                    // Ignore errors when trying to send disconnect message
                }
            }

            this.socket.close()
            this.socket = null
        }
    }

    public addEventListener(type: string, callback: (data: unknown) => void): void {
        // Store the callback in our internal map
        if (!this.listeners.has(type)) {
            this.listeners.set(type, [])
        }
        this.listeners.get(type)?.push(callback)
    }

    public removeEventListener(type: string, callback: (data: unknown) => void): void {
        // Remove from our internal map
        if (this.listeners.has(type)) {
            const listeners = this.listeners.get(type) || []
            this.listeners.set(
                type,
                listeners.filter((cb) => cb !== callback),
            )
        }
    }

    public send(data: unknown): void {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(data))
        } else {
            console.error('WebSocket is not connected, cannot send message')
        }
    }

    public isConnected(): boolean {
        return this.socket !== null && this.socket.readyState === WebSocket.OPEN
    }

    public isReconnecting(): boolean {
        return this.reconnectTimer !== null
    }
}

// Create and export a singleton instance
export default new WebSocketService('/realtime/ws/events/')
